import React from "react";
import { IconType } from "react-icons";
import { HiCheck, HiRefresh } from "react-icons/hi";

type PendingVariant = "todo" | "syncing";

type ConfigStatusItemProps = {
  icon: IconType;
  label: React.ReactNode;
  description?: React.ReactNode;
  completed: boolean;
  pendingVariant?: PendingVariant;
  action?: React.ReactNode;
  children?: React.ReactNode;
};

const StatusIcon = ({
  completed,
  pendingVariant = "todo",
}: {
  completed: boolean;
  pendingVariant?: PendingVariant;
}) => {
  if (completed) {
    return (
      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
        <HiCheck size={16} className="text-green-600" />
      </div>
    );
  }
  if (pendingVariant === "syncing") {
    return (
      <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center">
        <HiRefresh size={16} className="text-amber-600" />
      </div>
    );
  }
  return (
    <div className="w-6 h-6 border-2 border-gray-300 rounded-full flex items-center justify-center" />
  );
};

export const ConfigStatusItem: React.FC<ConfigStatusItemProps> = ({
  icon: Icon,
  label,
  description,
  completed,
  pendingVariant = "todo",
  action,
  children,
}) => {
  const borderColor = completed ? "border-green-200" : "border-gray-200";
  const bgColor = completed ? "bg-green-50" : "bg-white hover:border-gray-300";
  const iconBgColor = completed ? "bg-green-100" : "bg-gray-100";
  const iconTextColor = completed ? "text-green-600" : "text-gray-500";

  return (
    <div
      className={`p-4 rounded-lg border-2 transition-all duration-200 ${borderColor} ${bgColor}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center justify-between sm:justify-start gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className={`hidden sm:flex w-10 h-10 rounded-lg items-center justify-center shrink-0 ${iconBgColor}`}
            >
              <Icon size={20} className={iconTextColor} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 font-figtree">
                {label}
              </h3>
              {description && (
                <p className="text-xs text-gray-600 mt-1">{description}</p>
              )}
              {children}
            </div>
          </div>
          <div className="sm:hidden shrink-0">
            <StatusIcon completed={completed} pendingVariant={pendingVariant} />
          </div>
        </div>

        <div className="flex items-center gap-3 justify-end sm:flex-row-reverse shrink-0">
          <div className="hidden sm:block">
            <StatusIcon completed={completed} pendingVariant={pendingVariant} />
          </div>
          {action}
        </div>
      </div>
    </div>
  );
};

export default ConfigStatusItem;
